import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { CornerDownLeft, Search } from 'lucide-react';
import { useNavigate } from 'react-router';
import { cn } from '@/lib/cn';
import { Kbd } from '@/components/ui';
import type { NavGroup, PaletteCommand } from '@/lib/nav/types';

interface Entry {
  id: string;
  label: string;
  to: string;
  icon: PaletteCommand['icon'];
  section: 'Pages' | 'Actions';
  haystack: string;
}

function buildEntries(nav: readonly NavGroup[], commands: readonly PaletteCommand[]): Entry[] {
  const pages = nav.flatMap((group) =>
    group.items.map<Entry>((item) => ({
      id: `page-${item.id}`,
      label: item.label,
      to: item.to,
      icon: item.icon,
      section: 'Pages',
      haystack: [item.label, ...(item.keywords ?? [])].join(' ').toLowerCase(),
    })),
  );
  const actions = commands.map<Entry>((command) => ({
    id: `action-${command.id}`,
    label: command.label,
    to: command.to,
    icon: command.icon,
    section: 'Actions',
    haystack: [command.label, ...(command.keywords ?? [])].join(' ').toLowerCase(),
  }));
  return [...actions, ...pages];
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  nav: readonly NavGroup[];
  commands: readonly PaletteCommand[];
}

/** ⌘K: jump to any page or start an action without hunting through the sidebar. */
export function CommandPalette({ open, onClose, nav, commands }: CommandPaletteProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  const listId = useId();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const entries = useMemo(() => buildEntries(nav, commands), [nav, commands]);
  const results = useMemo(() => {
    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
    return tokens.length === 0 ? entries : entries.filter((entry) => tokens.every((token) => entry.haystack.includes(token)));
  }, [entries, query]);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }
    const onCancel = (event: Event): void => {
      event.preventDefault();
      onCloseRef.current();
    };
    const onPointerDown = (event: MouseEvent): void => {
      if (event.target === dialog) {
        onCloseRef.current();
      }
    };
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('mousedown', onPointerDown);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('mousedown', onPointerDown);
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || !open) {
      return;
    }
    dialog.showModal();
    setQuery('');
    setActive(0);
    return () => {
      if (dialog.open) {
        dialog.close();
      }
    };
  }, [open]);

  const go = (entry: Entry | undefined): void => {
    if (!entry) {
      return;
    }
    onClose();
    void navigate(entry.to);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (results.length ? (index + 1) % results.length : 0));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (results.length ? (index - 1 + results.length) % results.length : 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      go(results[active]);
    }
  };

  const activeEntry = results[active];
  let lastSection: Entry['section'] | null = null;

  return (
    <dialog
      ref={dialogRef}
      aria-label="Search and jump to"
      className="mx-auto mt-[12vh] w-[calc(100%-1.5rem)] max-w-xl animate-scale-in overflow-visible bg-transparent p-0 backdrop:bg-slate-900/45"
    >
      {open && (
        <div className="overflow-hidden rounded-xl bg-white shadow-pop">
          <div className="flex items-center gap-3 border-b border-slate-100 px-4">
            <Search className="size-4 shrink-0 text-slate-400" aria-hidden />
            <input
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-activedescendant={activeEntry ? `${listId}-${activeEntry.id}` : undefined}
              aria-label="Search pages and actions"
              onKeyDown={onKeyDown}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
              }}
              placeholder="Search pages and actions…"
              className="h-12 flex-1 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            <Kbd>Esc</Kbd>
          </div>
          <ul id={listId} role="listbox" aria-label="Results" className="scroll-thin max-h-80 overflow-y-auto p-2">
            {results.length === 0 && <li className="px-3 py-8 text-center text-sm text-slate-500">Nothing matches “{query}”.</li>}
            {results.map((entry, index) => {
              const showHeading = entry.section !== lastSection;
              lastSection = entry.section;
              const Icon = entry.icon;
              const selected = index === active;
              return (
                <li key={entry.id} role="presentation">
                  {showHeading && <p className="px-3 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wide text-slate-400">{entry.section}</p>}
                  <div
                    id={`${listId}-${entry.id}`}
                    role="option"
                    aria-selected={selected}
                    tabIndex={-1}
                    onMouseMove={() => setActive(index)}
                    onClick={() => go(entry)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        go(entry);
                      }
                    }}
                    className={cn('flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm', selected ? 'bg-brand-50 text-brand-900' : 'text-slate-700')}
                  >
                    <Icon className={cn('size-4 shrink-0', selected ? 'text-brand-700' : 'text-slate-400')} aria-hidden />
                    <span className="flex-1 truncate font-medium">{entry.label}</span>
                    {selected && <CornerDownLeft className="size-3.5 text-brand-500" aria-hidden />}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </dialog>
  );
}
